import React, { useState, useEffect, useContext, useCallback } from 'react';
import { getApolloContext } from '@apollo/client';
import { useParams, useHistory, Route, Switch, useLocation } from 'react-router-dom';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { getDealRoomBrandedName } from '../../../utils/locale';

// Import components
import SuccessionDealsHome from '../../../components/Succession/Deals/SuccessionDealsHome';
import DealTeaser from '../../../components/Succession/Deals/DealTeaser';
import DealProspects from '../../../components/Succession/Deals/DealProspects';
import DealWizardContainer from '../../../containers/Succession/Deals/DealWizardContainer';
import Loading from '../../../components/common/Loading';
import ErrorMessage from '../../../components/common/ErrorMessage';
import ScheduleMeetingContainer from '../ScheduleMeetingContainer';
import SuccessionThemeProvider from '../../../components/Succession/SuccessionThemeProvider';

// GraphQL queries
const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    company {
      id
      name
      host
      agent_word
      agent_word_fr
      deal_room_branded_name
      deal_room_branded_name_fr
      deal_room_single_word
      deal_room_single_word_fr
      stylesheet_name
      business_type
      features
      primary_color
      secondary_color
      accent_color
      neutral_color
      background_color
    }
    getCurrentUser {
      id
      name
      email
      locale
      motivation
      inventory_user {
        id
        can_match
        can_create_meeting_room
        # Get all deals, we'll filter for published ones in the component
        deals {
          id
          deal_name
          headline
          sub_headline
          list_price
          deal_state
          closed
          deal_type
          # These are calculated fields in the GraphQL type
          matches_count
          number_of_invites
          number_of_proposals
          number_of_ndas
          number_of_negotiations
        }
        # Get all deal interests, we'll filter them in the component
        deal_interests {
          id
          workflow_state
          saved
          invited
          shortlisted
          deal {
            id
            deal_name
            headline
            sub_headline
            list_price
            deal_state
            closed
            deal_type
            matches_count
            number_of_invites
            number_of_proposals
            number_of_ndas
            number_of_negotiations
          }
        }
      }
    }
  }
`;

const getDeal = gql`
  query GetDeal($dealId: String!) {
    getDeal(name: $dealId) {
      id
      deal_name
      headline
      sub_headline
      list_price
      deal_state
      closed
      deal_type
      summary_of_business
      ideal_candidate
      customer_demographics_comments
      products_and_services_comments
      growth_opportunities
      assets_included
      intangible_assets_included
      # Seller info
      inventory_user {
        id
        user {
          id
          allowed_name
          city
          province
          motivation
          
          # Client demographics
          avg_client_age
          number_of_clients
          niche_market
          assets_under_management
          # Financial data
          annual_revenue
          recurring_revenue
          gross_production
          year_1_gross
          year_2_gross
          year_3_gross
          year_4_gross
          years_practicing
          fee_based_revenue
          trailer_revenue
          direct_revenue
          transactional_revenue
          # Product mix
          tag_list
          percent_fee_based
          percent_commission
          percent_non_recurring_revenue
          percent_revenue_recurring
        }
      }
      
      # Interest data
      evaluate {
        id
        workflow_state
        saved
        invited
        shortlisted
        reason_for_suggestion
        receiving_party_nda_sent_timestamp
        proposal_letter
        inventory_user {
          id
          user {
            id
            name
            user_name
            headline
            city
            province
            assets_under_management
            annual_revenue
            fb_user_name
          }
        }
      }
      
      # For seller - all interests accessed via separate fields
      # Since deal_interests doesn't exist on DealType
      matches_count
      number_of_invites
      number_of_proposals
      number_of_ndas
      number_of_negotiations
      
      # Get these collections separately since they exist on the schema
      invited {
        id
        workflow_state
        # Partial Sales: where this invite came from (null otherwise).
        partial_sale_listing { id label closed }
        saved
        invited
        invitation_date
        shortlisted
        reason_for_suggestion
        deal_interest_type
        inventory_user {
          id
          user {
            id
            user_name
            headline
            city
            province
            years_practicing
            number_of_clients
            assets_under_management
            annual_revenue
            recurring_revenue
            fee_based_revenue
            trailer_revenue
            direct_revenue
            transactional_revenue
            fb_user_name
            motivation
            allowed_name
            profile_picture
          }
        }
      }
      shortlisted {
        id
        workflow_state
        # Partial Sales: where this invite came from (null otherwise).
        partial_sale_listing { id label closed }
        saved
        invited
        invitation_date
        shortlisted
        reason_for_suggestion
        deal_interest_type
        inventory_user {
          id
          user {
            id
            name
            user_name
            headline
            city
            province
            years_practicing
            number_of_clients
            assets_under_management
            annual_revenue
            recurring_revenue
            fee_based_revenue
            trailer_revenue
            direct_revenue
            transactional_revenue
            fb_user_name
            allowed_name
            profile_picture
          }
        } 
      }
      review_proposals {
        id
        workflow_state
        # Partial Sales: where this invite came from (null otherwise).
        partial_sale_listing { id label closed }
        saved
        invited
        invitation_date
        deal_interest_type
        inventory_user {
          id
          user {
            id
            name
            user_name
            headline
            city
            province
            years_practicing
            number_of_clients
            assets_under_management
            annual_revenue
            recurring_revenue
            fee_based_revenue
            trailer_revenue
            direct_revenue
            transactional_revenue
            fb_user_name
            allowed_name
            profile_picture
          }
        }
      }
      nda_out_for_review {
        id
        workflow_state
        # Partial Sales: where this invite came from (null otherwise).
        partial_sale_listing { id label closed }
        saved
        invited
        invitation_date
        deal_interest_type
        receiving_party_nda_sent_timestamp
        inventory_user {
          id
          user {
            id
            name
            user_name
            headline
            city
            province
            years_practicing
            number_of_clients
            assets_under_management
            annual_revenue
            recurring_revenue
            fee_based_revenue
            trailer_revenue
            fb_user_name
            allowed_name
            profile_picture
          }
        }
      }
      nda_awaiting_signature {
        id
        deal_interest_name
        workflow_state
        # Partial Sales: where this invite came from (null otherwise).
        partial_sale_listing { id label closed }
        saved
        invited
        invitation_date
        deal_interest_type
        disclosing_party_nda_sent_timestamp
        inventory_user {
          id
          user {
            id
            name
            user_name
            headline
            city
            province
            years_practicing
            number_of_clients
            assets_under_management
            annual_revenue
            recurring_revenue
            fee_based_revenue
            trailer_revenue
            fb_user_name
            allowed_name
            profile_picture
          }
        }
      }
      negotiating {
        id
        workflow_state
        # Partial Sales: where this invite came from (null otherwise).
        partial_sale_listing { id label closed }
        saved
        invited
        invitation_date
        deal_interest_type
        inventory_user {
          id
          user {
            id
            name
            user_name
            headline
            city
            province
            years_practicing
            number_of_clients
            assets_under_management
            annual_revenue
            recurring_revenue
            fee_based_revenue
            trailer_revenue
            fb_user_name
            allowed_name
            profile_picture
          }
        }
      }
      saved {
        id
        workflow_state
        saved
        invited
        invitation_date
        shortlisted
        reason_for_suggestion
        deal_interest_type
        inventory_user {
          id
          user {
            id
            name
            user_name
            headline
            city
            province
            years_practicing
            number_of_clients
            assets_under_management
            annual_revenue
            recurring_revenue
            fee_based_revenue
            trailer_revenue
            direct_revenue
            transactional_revenue
            fb_user_name
            allowed_name
            profile_picture
          }
        }  
      }
    }
  }
`;

const SuccessionDealsWrapper = ({ initialDealId, initialSection, company: initialCompany, userType, currentUser: initialCurrentUser }) => {
  // Get Apollo Client from context
  const { client } = useContext(getApolloContext());
  
  // Router hooks
  const routerParams = useParams();
  const history = useHistory();
  const location = useLocation();
  
  // Use router params if available, otherwise fall back to props
  const [dealId, setDealId] = useState(routerParams?.dealId || initialDealId);
  const [section, setSection] = useState(routerParams?.section || initialSection || 'home');
  
  // Set up the effect to extract dealId and section from the URL
  useEffect(() => {
    console.log("SuccessionDealsWrapper: URL changed, extracting parameters");
    
    const path = window.location.pathname;
    const pathParts = path.split('/');
    
    // Log the current path and extracted parts for debugging
    console.log("Current pathname:", path);
    console.log("Path parts:", pathParts);
    
    // Extract dealId from URL
    let urlDealId = null;
    let urlSection = null;
    
    if (pathParts.length >= 4 && pathParts[1] === 'succession' && pathParts[2] === 'deals') {
      urlDealId = pathParts[3];
      if (pathParts.length >= 5) {
        urlSection = pathParts[4];
      }
    }
    
    console.log("Extracted dealId:", urlDealId);
    console.log("Extracted section:", urlSection);
    
    // Set dealId if it's different from the current one
    if (urlDealId !== dealId) {
      console.log("Setting dealId to:", urlDealId);
      setDealId(urlDealId);
    }
    
    // Set section if it's different from the current one
    if (urlSection !== section) {
      console.log("Setting section to:", urlSection);
      setSection(urlSection);
    }
  }, [history.location.pathname]); // Listen to pathname changes
  
  // State
  const [isLoading, setIsLoading] = useState(!initialCurrentUser);
  const [error, setError] = useState(null);
  const [isDealOwner, setIsDealOwner] = useState(false);
  const [dealInterestId, setDealInterestId] = useState(null);
  
  // State for storing the data that would have come from Apollo hooks
  const [currentUser, setCurrentUser] = useState(initialCurrentUser || null);
  const [company, setCompany] = useState(initialCompany || null);
  const [dealData, setDealData] = useState(null);
  
  // Fetch current user data if not provided
  useEffect(() => {
    if (!currentUser) {
      setIsLoading(true);
      
      client.query({
        query: GET_CURRENT_USER
      })
      .then(result => {
        console.log('Fetched current user:', result.data.getCurrentUser);
        console.log('Fetched company:', result.data.company);
        setCurrentUser(result.data.getCurrentUser);
        setCompany(result.data.company);
        setIsLoading(false);
      })
      .catch(err => {
        console.error('Error fetching current user:', err);
        setError(err);
        setIsLoading(false);
      });
    }
  }, [client, currentUser]);
  
  // Fetch deal data when dealId changes
  useEffect(() => {
    if (dealId) {
      setIsLoading(true);
      
      client.query({
        query: getDeal,
        variables: { dealId }
      })
      .then(result => {
        console.log('Fetched deal data:', result.data.getDeal);
        setDealData(result.data);
        
        // Check if the current user is the deal owner
        const deal = result.data.getDeal;
        const isOwner = deal?.inventory_user?.user?.id === currentUser?.id;
        setIsDealOwner(isOwner);
        
        // Find the user's interest in this deal if they're not the owner
        if (!isOwner && currentUser?.inventory_user?.deal_interests) {
          const interest = currentUser.inventory_user.deal_interests.find(
            di => di?.deal?.deal_name === dealId
          );
          
          if (interest) {
            setDealInterestId(interest.id);
          }
        }
        
        setIsLoading(false);
      })
      .catch(err => {
        console.error('Error fetching deal data:', err);
        setError(err);
        setIsLoading(false);
      });
    }
  }, [client, dealId, currentUser]);
  
  // Function to refetch deal data
  const refetchDeal = () => {
    console.log('refetchDeal: Function called, dealId:', dealId);
    
    if (dealId) {
      console.log('refetchDeal: dealId exists, starting refetch...');
      setIsLoading(true);
      
      client.query({
        query: getDeal,
        variables: { dealId },
        fetchPolicy: 'network-only' // Bypass cache to get fresh data
      })
      .then(result => {
        console.log('refetchDeal: Refetched deal data successfully:', result.data.getDeal);
        // Update badge counts by logging them
        console.log('refetchDeal: Updated badge counts - Matches:', result.data.getDeal.matches_count);
        console.log('refetchDeal: Updated badge counts - Invited:', result.data.getDeal.number_of_invites);
        console.log('refetchDeal: Updated badge counts - Proposals:', result.data.getDeal.number_of_proposals);
        console.log('refetchDeal: Updated badge counts - NDAs:', result.data.getDeal.number_of_ndas);
        console.log('refetchDeal: Updated badge counts - Negotiations:', result.data.getDeal.number_of_negotiations);
        console.log('refetchDeal: Updated shortlisted count:', result.data.getDeal.shortlisted?.length || 0);
        
        setDealData(result.data);
        setIsLoading(false);
        console.log('refetchDeal: State updated, refetch complete');
      })
      .catch(err => {
        console.error('refetchDeal: Error refetching deal data:', err);
        setError(err);
        setIsLoading(false);
      });
    } else {
      console.log('refetchDeal: No dealId available, skipping refetch');
    }
  };
  
  // Navigation helper function
  const navigateTo = (path) => {
    history.push(path);
  };
  
  // Add event listeners for drawer events
  useEffect(() => {
    console.log('SuccessionDealsWrapper: Setting up navigation event listeners');
    
    let pendingNavigation = null;
    
    const handleShortlistNavigation = (event) => {
      console.log('SuccessionDealsWrapper: advisor-shortlisted-navigate event received!', event.detail);
      // Store the navigation target but don't navigate yet
      pendingNavigation = event.detail.targetUrl;
      
      // If refresh is requested, trigger refetch immediately
      if (event.detail.shouldRefresh) {
        console.log('SuccessionDealsWrapper: Refreshing data due to shortlist success');
        refetchDeal();
      }
    };
    
    const handleNdaNavigation = (event) => {
      console.log('SuccessionDealsWrapper: advisor-nda-sent-navigate event received!', event.detail);
      // Store the navigation target but don't navigate yet
      pendingNavigation = event.detail.targetUrl;
      
      // If refresh is requested, trigger refetch immediately
      if (event.detail.shouldRefresh) {
        console.log('SuccessionDealsWrapper: Refreshing data due to NDA sent success');
        refetchDeal();
      }
    };
    
    const handleDrawerClose = (event) => {
      console.log('SuccessionDealsWrapper: close-succession-drawer event received');
      // Navigate after drawer closes if there's a pending navigation
      if (pendingNavigation) {
        console.log('SuccessionDealsWrapper: Navigating to:', pendingNavigation);
        setTimeout(() => {
          navigateTo(pendingNavigation);
          pendingNavigation = null;
        }, 100); // Small delay to ensure drawer is fully closed
      }
    };
    
    window.addEventListener('advisor-shortlisted-navigate', handleShortlistNavigation);
    window.addEventListener('advisor-nda-sent-navigate', handleNdaNavigation);
    window.addEventListener('close-succession-drawer', handleDrawerClose);
    
    return () => {
      console.log('SuccessionDealsWrapper: Cleaning up navigation event listeners');
      window.removeEventListener('advisor-shortlisted-navigate', handleShortlistNavigation);
      window.removeEventListener('advisor-nda-sent-navigate', handleNdaNavigation);
      window.removeEventListener('close-succession-drawer', handleDrawerClose);
    };
  }, [navigateTo]);
  
  // Render the main navigation
  const renderMainNavigation = () => {
    // Navigation tabs removed - arrow tabs now handle all navigation
    return null;
  };
  
  // Render page header
  const renderPageHeader = () => {
    // Don't show header on wizard route
    if (location.pathname === '/succession/deals/wizard') return null;
    // Hide header on prospects pages per updated design
    if (location.pathname.includes('/succession/deals/') && location.pathname.includes('/prospects')) return null;
    if (location.pathname.includes('/succession/deals/') && location.pathname.includes('/teaser')) return null;
    
    // Get the deal room branded name
    const dealRoomName = getDealRoomBrandedName(company);
    
    return (
      <div className="bg-succession-bg-primary border-b border-succession-border">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <h1 className="text-2xl font-semibold text-succession-text-primary transition-colors duration-300">
                {dealRoomName}
              </h1>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Determine which component to render based on the current path
  const renderContent = () => {
    // Handle loading state
    if (isLoading) {
      return <Loading message="Loading deal data..." />;
    }
    
    // Handle error state
    if (error) {
      return <ErrorMessage message={error.message} />;
    }

    // NOTE: The "no deal selected" case (/succession/deals) is handled by the
    // <Route exact path="/succession/deals"> entry in the <Switch> below. That
    // route accounts for draft (new_deal) listings by redirecting to the wizard.
    // A previous early-return here rendered SuccessionDealsHome filtered to
    // `published` deals only, which shadowed that route entirely — so a seller
    // whose only deal was an unpublished draft landed on a blank page. Removed
    // so the Switch route runs and drafts are handled correctly.

  return (
    <div className="succession-deals-wrapper">
      {/* Page Header */}
      {renderPageHeader()}
      
      {/* Main navigation - only show when dealId is present and not on wizard route */}
      {dealId && location.pathname !== '/succession/deals/wizard' && renderMainNavigation()}
      
        {/* Content area - adjust the styles based on whether we have a dealId */}
        <div className={dealId ? "bg-succession-bg-secondary border border-succession-border rounded-lg shadow-sm p-4 mb-6 overflow-x-auto" : location.pathname === '/succession/deals' ? "overflow-x-auto" : "bg-succession-bg-primary p-6 overflow-x-auto"}>
        <Switch>
          <Route exact path="/succession/deals/wizard" render={() => <DealWizardContainer currentUser={currentUser} navigateTo={navigateTo} />} />
          <Route path="/succession/deals/:dealId/schedule-meeting/:dealInterestId" render={(props) => <ScheduleMeetingContainer deal={dealData?.getDeal} refetchDeal={refetchDeal} currentUser={currentUser} navigateTo={navigateTo} {...props} />} />
          <Route path="/succession/deals/:dealId/teaser" render={() => <DealTeaser 
            deal={dealData?.getDeal} 
            refetchDeal={refetchDeal}
            isDealOwner={isDealOwner}
            dealInterestId={dealInterestId}
            currentUser={currentUser}
            userMotivation={currentUser?.motivation}
            navigateTo={navigateTo}
          />} />
          <Route path="/succession/deals/:dealId/prospects/:stage/:subsection" render={(props) => <DealProspects deal={dealData?.getDeal} refetchDeal={refetchDeal} currentUser={currentUser} navigateTo={navigateTo} company={company} {...props} />} />
          <Route path="/succession/deals/:dealId/prospects/:stage" render={(props) => <DealProspects deal={dealData?.getDeal} refetchDeal={refetchDeal} currentUser={currentUser} navigateTo={navigateTo} company={company} {...props} />} />
          <Route path="/succession/deals/:dealId/prospects" render={(props) => <DealProspects deal={dealData?.getDeal} refetchDeal={refetchDeal} currentUser={currentUser} navigateTo={navigateTo} company={company} {...props} />} />
          <Route path="/succession/deals/:dealId" render={() => {
            const deal = dealData?.getDeal;
            const isOwner = deal?.inventory_user?.user?.id === currentUser?.id;
            
            // For published deals, default to prospects view for deal owners, teaser for others
            if (deal?.deal_state === 'published' && isOwner) {
              navigateTo(`/succession/deals/${dealId}/prospects`);
              return <Loading message="Redirecting to prospects..." />;
            }
            
            return <DealTeaser 
              deal={deal} 
              refetchDeal={refetchDeal} 
              isDealOwner={isOwner} 
              dealInterestId={dealInterestId} 
              currentUser={currentUser} 
              userMotivation={currentUser?.motivation} 
              navigateTo={navigateTo} 
            />;
          }} />
          <Route exact path="/succession/deals" render={() => {
            console.log('=== /succession/deals Route ===');
            console.log('currentUser:', currentUser);
            console.log('currentUser.inventory_user:', currentUser?.inventory_user);
            console.log('currentUser.inventory_user.deals:', currentUser?.inventory_user?.deals);
            
            // Get all user's deals (not just published ones for this check)
            const allUserDeals = currentUser?.inventory_user?.deals || [];
            console.log('allUserDeals:', allUserDeals);
            console.log('allUserDeals.length:', allUserDeals.length);
            
            // Check if user has at least one deal
            if (allUserDeals.length > 0) {
              console.log('User has deals - checking for redirect...');
              // Check if any deal has workflow state "new_deal"
              const newDeal = allUserDeals.find(deal => deal.deal_state === 'new_deal');
              
              if (newDeal) {
                navigateTo('/succession/deals/wizard');
                return <Loading message="Redirecting to deal wizard..." />;
              }
              
              // Otherwise, redirect to prospects page of the first deal
              const firstDeal = allUserDeals[0];
              navigateTo(`/succession/deals/${firstDeal.deal_name}/prospects`);
              return <Loading message="Redirecting to prospects..." />;
            }
            
            // If no deals, show the home page as normal
            const activeDeals = currentUser?.inventory_user?.deals?.filter(
              deal => !deal?.closed
            ) || [];
            
            const evaluatingDeals = currentUser?.inventory_user?.deal_interests
              ?.filter(di => di.workflow_state !== 'matching' && di.workflow_state !== 'negotiating')
              ?.filter(di => di?.deal && !di?.deal?.closed) || [];
            
            return (
              <SuccessionThemeProvider company={company || {}} user={currentUser || {}}>
                <SuccessionDealsHome
                  deals={activeDeals}
                  initialEvaluatingDeals={evaluatingDeals}
                  currentUser={currentUser}
                  navigateTo={navigateTo}
                  company={company}
                />
              </SuccessionThemeProvider>
            );
          }} />
        </Switch>
      </div>
    </div>
  );
};
  return renderContent();
};

export default SuccessionDealsWrapper; 